import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search,
  Building2,
  Home as HomeIcon,
  Warehouse,
  ArrowRight,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PROPERTY_TYPES,
  OPERATION_TYPES,
  ESTADO_OPTIONS,
  formatPropertyStatus,
  formatOperationTypes,
  formatEstado,
  formatPropertyPrice,
  formatPricePerM2,
  getPropertyPrice,
  isPriceOnRequest,
} from "../utils/formatters";
import {
  PRICING_PLANS,
  getAssignedPlan,
  getDerivedPaymentStatus,
  getTrialDaysCopy,
} from "../constants/plans";
import { useAuth } from "../App";
import { api } from "../services/apiClient";
import { useSubscriptionActivation } from "../hooks/useSubscriptionActivation";

const CATEGORIES = [
  {
    value: "Residencial",
    label: "Residencial",
    icon: HomeIcon,
    color: "hsl(145, 50%, 45%)",
  },
  {
    value: "Comercial",
    label: "Comercial",
    icon: Building2,
    color: "hsl(210, 60%, 50%)",
  },
  {
    value: "Industrial",
    label: "Industrial",
    icon: Warehouse,
    color: "hsl(25, 55%, 50%)",
  },
];

const PLAN_ICON_BY_ID = {
  free: HomeIcon,
  pro: HomeIcon,
  broker: Building2,
  developer: Warehouse,
};

const PLAN_ICON_STYLE_BY_ID = {
  free: { iconBg: "bg-primary/10", iconColor: "text-primary" },
  pro: { iconBg: "bg-primary/10", iconColor: "text-primary" },
  broker: { iconBg: "bg-accent/10", iconColor: "text-accent" },
  developer: { iconBg: "bg-secondary/10", iconColor: "text-secondary" },
};

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [accountUser, setAccountUser] = useState(user || null);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [searchParams, setSearchParams] = useState({
    location: "",
    category: "all",
    property_type: "all",
    estado: "all",
    status: "all",
  });

  const fetchFeaturedProperties = async () => {
    try {
      const response = await api.get(
        "/properties?featured_only=true&limit=10",
        { auth: false },
      );
      const data = await response.json();
      setFeaturedProperties(data);
    } catch (error) {
      console.error("Error fetching featured properties:", error);
    }
  };

  useEffect(() => {
    fetchFeaturedProperties();
  }, []);

  useEffect(() => {
    let mounted = true;

    const syncAccountUser = async () => {
      if (!user) {
        if (mounted) setAccountUser(null);
        return;
      }

      if (mounted) setAccountUser(user);

      try {
        const response = await api.get("/auth/me");
        if (!response.ok) return;
        const data = await response.json();
        if (mounted) setAccountUser(data);
      } catch {
        // Keep local auth context user when /auth/me is temporarily unavailable.
      }
    };

    syncAccountUser();
    return () => {
      mounted = false;
    };
  }, [user]);

  const activeUser = accountUser || user;
  const assignedPlan = activeUser ? getAssignedPlan(activeUser) : null;
  const { activateSubscription, activatingPlanId } = useSubscriptionActivation({
    user: activeUser,
    navigate,
  });

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchParams.category !== "all")
      params.append("category", searchParams.category);
    if (searchParams.property_type !== "all")
      params.append("property_type", searchParams.property_type);
    if (searchParams.estado !== "all")
      params.append("estado", searchParams.estado);
    if (searchParams.status !== "all")
      params.append("status", searchParams.status);
    navigate(`/search?${params.toString()}`);
  };

  const availablePropertyTypes =
    searchParams.category !== "all" && PROPERTY_TYPES[searchParams.category]
      ? PROPERTY_TYPES[searchParams.category]
      : [];

  return (
    <div className="min-h-screen">
      <Header />

      <section
        className="relative min-h-[66vh] md:h-[80vh] lg:h-[90vh] pt-14 sm:pt-16 md:pt-20 flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center py-16 md:py-20 lg:py-24">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 md:mb-10 lg:mb-12 leading-tight tracking-tight"
            style={{ fontFamily: "Playfair Display, serif" }}
            data-testid="hero-title"
          >
            Descubre nuestra red global inmobiliaria
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-white/95 backdrop-blur-xl rounded-lg p-5 sm:p-6 md:p-8 shadow-2xl max-w-3xl mx-auto"
            data-testid="search-form"
          >
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs sm:text-sm font-semibold mb-2 block text-gray-800">
                    Categoría
                  </label>
                  <Select
                    value={searchParams.category}
                    onValueChange={(value) =>
                      setSearchParams({
                        ...searchParams,
                        category: value,
                        property_type: "all",
                      })
                    }
                  >
                    <SelectTrigger
                      className="w-full h-12 bg-white"
                      data-testid="category-select"
                    >
                      <SelectValue placeholder="Selecciona categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="Residencial">Residencial</SelectItem>
                      <SelectItem value="Comercial">Comercial</SelectItem>
                      <SelectItem value="Industrial">Industrial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs sm:text-sm font-semibold mb-2 block text-gray-800">
                    Tipo de Propiedad
                  </label>
                  <Select
                    value={searchParams.property_type}
                    onValueChange={(value) =>
                      setSearchParams({ ...searchParams, property_type: value })
                    }
                  >
                    <SelectTrigger
                      className="w-full h-12 bg-white"
                      data-testid="property-type-select"
                    >
                      <SelectValue placeholder="Selecciona tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {availablePropertyTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs sm:text-sm font-semibold mb-2 block text-gray-800">
                    Estado
                  </label>
                  <Select
                    value={searchParams.estado}
                    onValueChange={(value) =>
                      setSearchParams({ ...searchParams, estado: value })
                    }
                  >
                    <SelectTrigger
                      className="w-full h-12 bg-white"
                      data-testid="estado-select"
                    >
                      <SelectValue placeholder="Nuevo / Segunda Mano" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {ESTADO_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs sm:text-sm font-semibold mb-2 block text-gray-800">
                    Tipo de Operación
                  </label>
                  <Select
                    value={searchParams.status}
                    onValueChange={(value) =>
                      setSearchParams({ ...searchParams, status: value })
                    }
                  >
                    <SelectTrigger
                      className="w-full h-12 bg-white"
                      data-testid="status-select"
                    >
                      <SelectValue placeholder="Selecciona operación" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {OPERATION_TYPES.map((op) => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 sm:h-12 bg-primary hover:bg-primary/90 text-primary-foreground text-sm sm:text-base font-semibold"
                data-testid="search-button"
              >
                <Search className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Buscar Propiedades
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-16 lg:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 tracking-tight"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
              data-testid="categories-title"
            >
              Explora por Categoría
            </h2>
            <p className="text-base md:text-lg text-muted-foreground px-4">
              Encuentra activos alineados con tu objetivo, etapa y presupuesto
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto">
            {CATEGORIES.map((category, index) => {
              const Icon = category.icon;
              return (
                <motion.div
                  key={category.value}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  onClick={() => navigate(`/search?category=${category.value}`)}
                  className="bg-background border border-border/40 rounded-lg p-6 md:p-8 hover:-translate-y-1 hover:shadow-xl hover:border-primary/40 transition-all duration-300 cursor-pointer touch-manipulation"
                  data-testid={`category-card-${category.value}`}
                >
                  <div
                    className="w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center mb-4"
                    style={{ backgroundColor: category.color }}
                  >
                    <Icon className="w-7 h-7 md:w-8 md:h-8 text-white" />
                  </div>
                  <h3
                    className="text-lg md:text-xl font-bold mb-2"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    {category.label}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Ver propiedades
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section
        className="py-12 md:py-16 px-4 sm:px-6 bg-primary/5 border-y border-primary/10"
        data-testid="publish-search-section"
      >
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Search className="w-10 h-10 text-primary mx-auto mb-4" />
            <h3
              className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 tracking-tight"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            >
              ¿No encuentras lo que buscas?
            </h3>
            <p className="text-base md:text-lg text-muted-foreground mb-6 px-4">
              Publica tu necesidad y recibe respuestas de propietarios,
              asesores y desarrolladoras con contexto desde el inicio.
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/create-request")}
              className="text-sm sm:text-base px-6 sm:px-8 py-4 sm:py-5 w-full sm:w-auto"
              data-testid="publish-search-button"
            >
              Publicar búsqueda
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {featuredProperties.length > 0 && (
        <section className="py-12 md:py-16 lg:py-24 px-4 sm:px-6 bg-muted">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 md:mb-12 gap-4">
              <div>
                <h2
                  className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 tracking-tight"
                  style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
                  data-testid="featured-properties-title"
                >
                  Propiedades Destacadas
                </h2>
                <p className="text-base md:text-lg text-muted-foreground">
                  Selección activa para avanzar con más contexto
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate("/search")}
                className="w-full sm:w-auto"
                data-testid="view-all-button"
              >
                Ver Todas
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
              {featuredProperties.map((property, index) => (
                <motion.div
                  key={property.id || property._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  onClick={() =>
                    navigate(`/property/${property.id || property._id}`)
                  }
                  className="bg-background border border-border/40 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:border-primary/40 transition-all duration-300 cursor-pointer touch-manipulation relative"
                  data-testid={`property-card-${property.id || property._id}`}
                >
                  {property.is_featured && (
                    <div className="absolute top-2 right-2 z-10 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                      ⭐ Destacada
                    </div>
                  )}
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={
                        property.image_urls?.[0] ||
                        "https://images.pexels.com/photos/35193584/pexels-photo-35193584.jpeg"
                      }
                      alt={property.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4 md:p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className="text-xs uppercase tracking-wider font-semibold"
                        style={{
                          color: CATEGORIES.find(
                            (c) => c.value === property.category,
                          )?.color,
                        }}
                      >
                        {property.category}
                        {property.property_type || property.subcategory
                          ? ` · ${property.property_type || property.subcategory}`
                          : ""}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatOperationTypes(
                          property.operation_types,
                          property.status,
                        )}
                      </span>
                    </div>
                    <h3
                      className="text-lg md:text-xl font-bold mb-2 line-clamp-2"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {property.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-1">
                      {property.address}
                    </p>
                    <div className="flex items-end justify-between">
                      <div>
                        <p
                          className="text-xl md:text-2xl font-bold mono"
                          data-testid={`property-price-${property.id || property._id}`}
                        >
                          {formatPropertyPrice(property)}
                        </p>
                        {property.construction_size_m2 &&
                          !isPriceOnRequest(property) &&
                          getPropertyPrice(property).price && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatPricePerM2(
                                getPropertyPrice(property).price,
                                property.construction_size_m2,
                                getPropertyPrice(property).currency,
                              )}
                            </p>
                          )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 md:py-20 lg:py-32 px-4 sm:px-6 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 tracking-tight"
            style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
          >
            Encuentra mejores oportunidades desde hoy.
          </h2>
          <p className="text-base md:text-lg mb-6 md:mb-8 opacity-90 px-4">
            Crea una cuenta gratuita o activa el plan que mejor se adapta a tu operación.
          </p>
          <Button
            size="lg"
            variant="secondary"
            onClick={() => navigate("/search")}
            className="text-sm sm:text-base md:text-lg px-5 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 w-full sm:w-auto"
            data-testid="cta-button"
          >
            Explorar oportunidades
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Planes y Precios */}
      <section className="py-16 md:py-24 px-6 bg-muted">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-3xl md:text-5xl font-bold mb-4 tracking-tight"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            >
              Elige la cuenta que acompaña tu operación
            </h2>
            <p className="text-lg text-muted-foreground">
              Cada rol activa un plan, límites claros y herramientas pensadas para esa etapa del negocio.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRICING_PLANS.map((plan) => {
              const Icon = PLAN_ICON_BY_ID[plan.planId] || HomeIcon;
              const iconStyle =
                PLAN_ICON_STYLE_BY_ID[plan.planId] || PLAN_ICON_STYLE_BY_ID.free;
              const isCurrent = !!activeUser && assignedPlan === plan.planId;
              const isAdmin = activeUser?.role === "admin";
              const canStartTrialHere =
                !!activeUser &&
                assignedPlan === plan.planId &&
                !["active", "trialing"].includes(getDerivedPaymentStatus(activeUser));
              const registerUrl = `/register?role=${plan.role}`;
              return (
                <div
                  key={plan.planId}
                  className={`bg-background rounded-lg p-6 flex flex-col relative ${isCurrent ? "border-2 border-primary shadow-lg ring-2 ring-primary/20" : plan.highlighted ? "border-2 border-accent shadow-lg" : "border border-border"}`}
                  data-testid={`home-plan-${plan.planId}`}
                >
                  {isCurrent && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-xs font-semibold">
                        Tu Plan Actual
                      </span>
                    </div>
                  )}
                  {!isCurrent && plan.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-accent text-white px-4 py-1 rounded-full text-xs font-semibold">
                        {plan.badge}
                      </span>
                    </div>
                  )}
                  <div
                    className={`w-14 h-14 ${iconStyle.iconBg} rounded-full flex items-center justify-center mx-auto mb-4 ${isCurrent || plan.badge ? "mt-2" : ""}`}
                  >
                    <Icon className={`w-7 h-7 ${iconStyle.iconColor}`} />
                  </div>
                  <h3 className="text-xl font-bold mb-1 text-center">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3 text-center">
                    {plan.description}
                  </p>
                  <p className="text-3xl font-bold mb-4 text-center">
                    ${plan.price.toLocaleString()}
                    <span className="text-sm text-muted-foreground">/mes</span>
                  </p>
                  {plan.trialDays ? (
                    <p className="text-[11px] uppercase tracking-wide font-semibold text-primary text-center mb-4">
                      Incluye {getTrialDaysCopy(plan.trialDays)} de prueba
                    </p>
                  ) : null}
                  <ul className="text-sm space-y-2 mb-6 flex-1">
                    {plan.features.map((f, i) => (
                      <li key={i}>&#10003; {f}</li>
                    ))}
                  </ul>
                  <Button
                    variant={
                      isCurrent
                        ? "secondary"
                        : plan.highlighted
                          ? "default"
                          : "outline"
                    }
                    disabled={isCurrent || isAdmin}
                    onClick={() => {
                      activateSubscription({
                        planId: plan.id,
                        role: new URLSearchParams(
                          plan.registerUrl.split("?")[1],
                        ).get("role"),
                      });
                    }}
                    className="w-full"
                    data-testid={`home-plan-btn-${plan.planId}`}
                  >
                    {isCurrent
                      ? "Plan Actual"
                      : isAdmin
                        ? "Cuenta Admin"
                        : activatingPlanId === plan.id
                          ? "Redirigiendo..."
                        : activeUser
                          ? canStartTrialHere
                            ? `Activar ${getTrialDaysCopy(plan.trialDays)}`
                            : "Ver plan"
                          : plan.cta}
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/pricing")}
            >
              Ver planes, límites y preguntas frecuentes
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
